import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { type ActionName, AnnouncementActions } from "../../components/admin/announcements/AnnouncementActions";
import { AnnouncementCounts } from "../../components/admin/announcements/AnnouncementCounts";
import own from "../../components/admin/announcements/Announcements.module.css";
import { DELIVERY_HEADERS, DeliveryTable } from "../../components/admin/announcements/DeliveryTable";
import { AdminTableSkeleton } from "../../components/admin/AdminTableSkeleton";
import { Toast, useToast } from "../../components/Toast";
import { type AnnouncementSummary, useAdminAnnouncement } from "../../hooks/useAdminAnnouncements";
import { readsAs, STATE_LABEL } from "../../lib/announcements";
import { formatDateTime } from "../../utils/formatDateTime";
import styles from "../AdminInstances.module.css";

const SUCCESS: Record<ActionName, string> = {
  send: "Sending. Each instance reports back as it goes.",
  retry: "Retrying the instances it did not reach.",
  cancel: "Cancelled. Anything already on its way will still finish.",
};

// One announcement, one row per instance: the dry run's verdict before sending, each box's
// report after.
export const AdminAnnouncementDetail = (): JSX.Element => {
  const { announcementId = "" } = useParams<{ announcementId: string }>();
  const { toast, showToast, dismissToast } = useToast();
  const query = useAdminAnnouncement(announcementId);
  const [busy, setBusy] = useState<boolean>(false);
  const { detail } = query;

  const handleAction = async (action: ActionName): Promise<void> => {
    setBusy(true);
    const result = await query[action]();
    setBusy(false);
    showToast(result.ok ? SUCCESS[action] : result.message, result.ok ? "success" : "error");
  };

  return (
    <div className={styles.page}>
      <DetailHeader summary={detail?.announcement ?? null} />
      {query.status === "error" && <div className={styles.empty}>{query.error ?? "Failed to load the announcement."}</div>}
      {detail !== null && (
        <section className={own.composer}>
          <div className={own.label}>Customers read</div>
          <div className={`${own.readsAs} ${own.body}`} data-testid="announcement-detail-body">
            {readsAs(detail.announcement.body)}
          </div>
          <AnnouncementCounts summary={detail.announcement} />
          <AnnouncementActions detail={detail} busy={busy} onAction={(action) => void handleAction(action)} />
          <span className={own.hint}>
            Unknown means a box restarted mid-send, so it may or may not have arrived. It is never resent automatically.
          </span>
        </section>
      )}
      <section className={styles.listSection}>
        {query.status === "loading" && (
          <AdminTableSkeleton headers={DELIVERY_HEADERS} tableClassName={`${styles.table ?? ""} ${own.compactTable ?? ""}`} />
        )}
        {detail !== null && detail.deliveries.length > 0 && (
          <DeliveryTable deliveries={detail.deliveries} isFetching={query.isFetching} />
        )}
      </section>
      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

const DetailHeader = ({ summary }: { summary: AnnouncementSummary | null }): JSX.Element => (
  <header className={styles.panelIntro}>
    <Link className={`${own.link} ${own.backLink}`} to="/dashboard/admin/announcements">
      ← Announcements
    </Link>
    <h2 className={styles.panelTitle}>
      Announcement{" "}
      {summary !== null && (
        <span className={own[STATE_LABEL[summary.state].tone]} data-testid="announcement-state">
          {STATE_LABEL[summary.state].label}
        </span>
      )}
    </h2>
    {summary !== null && (
      <p className={`${styles.panelHint} ${own.intro}`}>
        Written by {summary.createdBy}, {formatDateTime(summary.createdAt)}
        {summary.sentAt !== null && `. Sent by ${summary.sentBy}, ${formatDateTime(summary.sentAt)}`}
      </p>
    )}
  </header>
);
